/**
 * Inline detail panel rendered beneath an expanded record row in
 * `ChannelRecordsModal` (C7-FE). Read-only: the record's labels by
 * human-readable name (grouped per dimension) plus key context.
 *
 * Label source:
 *   - finalised / agreed records carry the final `goldLabels` on the row
 *     → render directly, no fetch.
 *   - disputed / in-flight records (`goldLabels === null`) → lazily fetch
 *     the full detail (`useDatasetRecord`, gated by `enabled`) and show
 *     the current first + second annotation passes and the resolution.
 *
 * Label-id → name comes from the collection's pinned taxonomy version,
 * resolved once at the modal level and passed down as `labelNamesByDim`.
 * An id missing from that map degrades to the raw id (never crashes); the
 * same holds if the taxonomy is still loading (the dimension id is shown
 * in place of its display name).
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { formatTimeAgo } from '../../../../../../utils/dateUtils';
import { statusBadge } from '../../../annotate/components/statusBadge';
import { useDatasetRecord } from '../../../annotate/hooks/useAnnotation';
import type { DimensionLabels, UserSummary } from '../../../annotate/types';
import { CHANNELS, type ChannelKey } from '../../../shared/channels';
import { getDimensionColor } from '../../../shared/dimensions';
import type { TaxonomyDimension } from '../../../types/taxonomy';
import type { DatasetRecord } from '../../types';

interface Props {
  record: DatasetRecord;
  dimensions: TaxonomyDimension[];
  labelNamesByDim: Record<string, Record<string, string>>;
  namesByAnnotatorId?: Record<string, UserSummary>;
}

/** "abcd1234…" — mirrors the modal's annotator fallback. */
const uuidFallback = (id: string): string => `${id.slice(0, 8)}…`;

const annotatorName = (
  id: string | null,
  byId: Record<string, UserSummary> | undefined,
): string => {
  if (!id) return '—';
  const name = byId?.[id]?.name;
  if (name && name.trim().length > 0) return name;
  return uuidFallback(id);
};

/**
 * Render a `DimensionLabels` map as per-dimension chip groups. Dimensions
 * are listed in taxonomy order; any label-map key not in the taxonomy
 * (version mismatch, or taxonomy still loading) is appended after, keyed
 * by its raw id. Empty / missing maps render a muted placeholder.
 */
const LabelGroups: React.FC<{
  labels: DimensionLabels;
  dimensions: TaxonomyDimension[];
  labelNamesByDim: Record<string, Record<string, string>>;
}> = ({ labels, dimensions, labelNamesByDim }) => {
  const withLabels = (id: string): boolean => (labels[id]?.length ?? 0) > 0;
  const taxonomyIds = dimensions.map((d) => d.id).filter(withLabels);
  const knownIds = new Set(dimensions.map((d) => d.id));
  const leftoverIds = Object.keys(labels).filter(
    (id) => withLabels(id) && !knownIds.has(id),
  );
  const orderedIds = [...taxonomyIds, ...leftoverIds];
  const nameByDimId = new Map(dimensions.map((d) => [d.id, d.name]));

  if (orderedIds.length === 0) {
    return (
      <p
        className="text-[12px] italic text-muted-foreground"
        data-testid="record-detail-no-labels"
      >
        No labels.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {orderedIds.map((dimId) => {
        const ids = labels[dimId] ?? [];
        const names = labelNamesByDim[dimId] ?? {};
        return (
          <div
            key={dimId}
            className="flex flex-col gap-1"
            data-testid={`record-detail-dim-${dimId}`}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: getDimensionColor(dimId) }}
                aria-hidden
              />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {nameByDimId.get(dimId) ?? dimId}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {ids.map((labelId) => (
                <Badge
                  key={labelId}
                  variant="secondary"
                  data-testid={`record-detail-label-${dimId}-${labelId}`}
                >
                  {names[labelId] ?? labelId}
                </Badge>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** One labelled label-set section (a pass or the resolution). */
const LabelSection: React.FC<{
  testid: string;
  heading: string;
  labels: DimensionLabels | undefined;
  emptyText: string;
  note?: string | null;
  noteLabel?: string;
  dimensions: TaxonomyDimension[];
  labelNamesByDim: Record<string, Record<string, string>>;
}> = ({
  testid,
  heading,
  labels,
  emptyText,
  note,
  noteLabel,
  dimensions,
  labelNamesByDim,
}) => (
  <section data-testid={testid}>
    <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {heading}
    </h4>
    {labels ? (
      <LabelGroups
        labels={labels}
        dimensions={dimensions}
        labelNamesByDim={labelNamesByDim}
      />
    ) : (
      <p className="text-[12px] italic text-muted-foreground">{emptyText}</p>
    )}
    {note && note.trim().length > 0 && (
      <p className="mt-2 text-[12px] italic text-muted-foreground">
        <span className="font-semibold not-italic">{noteLabel}: </span>
        {note}
      </p>
    )}
  </section>
);

const RecordDetailPanel: React.FC<Props> = ({
  record,
  dimensions,
  labelNamesByDim,
  namesByAnnotatorId,
}) => {
  // Gold pool (finalised / agreed) carries the final label set on the row
  // — no fetch. Everything else fetches the passes + resolution lazily.
  const needsFetch = record.goldLabels === null;
  const detail = useDatasetRecord(record.id, { enabled: needsFetch });

  const channelMeta =
    CHANNELS[record.sourceMetadata.channel as ChannelKey] ?? null;
  const badge = statusBadge(record.status);

  const firstAnnotation = detail.data?.firstAnnotation ?? null;
  const secondAnnotation = detail.data?.secondAnnotation ?? null;
  const resolution = detail.data?.resolution ?? null;

  const passHeading = (base: string, annotatorId: string | null): string => {
    const name = annotatorId
      ? annotatorName(annotatorId, namesByAnnotatorId)
      : null;
    return name && name !== '—' ? `${base} · ${name}` : base;
  };

  return (
    <div
      className="space-y-4 px-4 py-4"
      data-testid={`record-detail-panel-${record.id}`}
    >
      {/* Context block */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {channelMeta && (
            <Badge
              variant="outline"
              style={{
                borderColor: `${channelMeta.color}40`,
                color: channelMeta.color,
              }}
            >
              {channelMeta.name}
            </Badge>
          )}
          {record.sourceMetadata.journey && (
            <Badge variant="secondary">{record.sourceMetadata.journey}</Badge>
          )}
          {record.sourceMetadata.product && (
            <Badge variant="secondary">{record.sourceMetadata.product}</Badge>
          )}
          <span className="text-[11px] text-muted-foreground">
            {record.interactionDate}
          </span>
        </div>
        <p
          className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground"
          data-testid="record-detail-verbatim"
        >
          {record.verbatim}
        </p>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-muted-foreground">
          <div className="flex gap-1">
            <dt className="font-semibold">1st annotator:</dt>
            <dd>{annotatorName(record.firstAnnotatorId, namesByAnnotatorId)}</dd>
          </div>
          <div className="flex gap-1">
            <dt className="font-semibold">2nd annotator:</dt>
            <dd>{annotatorName(record.secondAnnotatorId, namesByAnnotatorId)}</dd>
          </div>
          <div className="flex gap-1">
            <dt className="font-semibold">Created:</dt>
            <dd>{formatTimeAgo(record.createdAt)}</dd>
          </div>
          {record.finalisedAt && (
            <div className="flex gap-1">
              <dt className="font-semibold">Finalised:</dt>
              <dd>{formatTimeAgo(record.finalisedAt)}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Labels */}
      <div className="space-y-3 border-t border-border pt-3">
        {!needsFetch ? (
          <LabelSection
            testid="record-detail-gold"
            heading="Gold labels"
            labels={record.goldLabels ?? {}}
            emptyText="No gold labels."
            dimensions={dimensions}
            labelNamesByDim={labelNamesByDim}
          />
        ) : detail.isLoading ? (
          <div
            className="py-4 text-center text-[12px] text-muted-foreground"
            data-testid="record-detail-loading"
          >
            Loading record details…
          </div>
        ) : detail.isError ? (
          <div
            className="py-4 text-center text-[12px] text-rose-600"
            data-testid="record-detail-error"
          >
            Couldn&rsquo;t load full record details.
          </div>
        ) : detail.data ? (
          <>
            <LabelSection
              testid="record-detail-first-pass"
              heading={passHeading('First pass', firstAnnotation?.annotatorId ?? null)}
              labels={firstAnnotation?.labels}
              emptyText="First pass not yet submitted."
              note={firstAnnotation?.confidenceNote}
              noteLabel="Confidence note"
              dimensions={dimensions}
              labelNamesByDim={labelNamesByDim}
            />
            <LabelSection
              testid="record-detail-second-pass"
              heading={passHeading('Second pass', secondAnnotation?.annotatorId ?? null)}
              labels={secondAnnotation?.labels}
              emptyText="Second pass not yet submitted."
              note={secondAnnotation?.confidenceNote}
              noteLabel="Confidence note"
              dimensions={dimensions}
              labelNamesByDim={labelNamesByDim}
            />
            {resolution && (
              <LabelSection
                testid="record-detail-resolution"
                heading="Resolution"
                labels={resolution.labels}
                emptyText="No resolution labels."
                note={resolution.rationale}
                noteLabel="Rationale"
                dimensions={dimensions}
                labelNamesByDim={labelNamesByDim}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default RecordDetailPanel;
